import React, { useMemo, useState } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { OccurrenceCount } from '../types';
import './FilterButton.scss';

interface ReviewTimeLineProps {
    data: OccurrenceCount[];
}

type Granularity = 'daily' | 'weekly' | 'monthly' | 'quarterly';

interface GranularityOption {
    value: Granularity;
    label: string;
}

const granularityOptions: GranularityOption[] = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
];

const DAYS_IN_DAILY_RANGE = 60;
const WEEKS_IN_WEEKLY_RANGE = 26;
const MONTHS_IN_MONTHLY_RANGE = 24;
const QUARTERS_IN_QUARTERLY_RANGE = 12;

const MONTH_SHORT_NAMES = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];

const parseDate = (dateStr: string): Date => {
    const [day, month, year] = dateStr.split('/').map(Number);
    return new Date(year, month - 1, day);
};

const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

// Returns the Monday of the week containing the given date.
const getWeekStart = (date: Date): Date => {
    const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const day = result.getDay();
    // Treat Monday as the start of the week. getDay(): Sun=0..Sat=6
    const diff = (day + 6) % 7;
    result.setDate(result.getDate() - diff);
    return result;
};

const ReviewTimeLine: React.FC<ReviewTimeLineProps> = ({ data }) => {
    const [granularity, setGranularity] = useState<Granularity>('daily');

    // Build a lookup of daily counts keyed by DD/MM/YYYY.
    const dailyCountMap = useMemo(() => {
        const map = new Map<string, number>();
        data.forEach((item) => {
            try {
                map.set(item.name, item.occurrence);
            } catch (error) {
                console.error(
                    `Error processing data item: ${item.name}`,
                    error,
                );
            }
        });
        return map;
    }, [data]);

    const { timelineData, tickRotation, bottomMargin } = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (granularity === 'daily') {
            const points = [];
            for (let i = DAYS_IN_DAILY_RANGE - 1; i >= 0; i--) {
                const date = new Date(today);
                date.setDate(today.getDate() - i);
                const key = formatDate(date);
                points.push({
                    x: key,
                    y: dailyCountMap.get(key) ?? 0,
                });
            }
            return {
                timelineData: [{ id: 'Reviews', data: points }],
                tickRotation: -45,
                bottomMargin: 60,
            };
        }

        if (granularity === 'weekly') {
            // Aggregate all known daily counts into their week-start bucket.
            const weekTotals = new Map<number, number>();
            dailyCountMap.forEach((count, dateKey) => {
                const weekStart = getWeekStart(parseDate(dateKey));
                const ts = weekStart.getTime();
                weekTotals.set(ts, (weekTotals.get(ts) ?? 0) + count);
            });

            const currentWeekStart = getWeekStart(today);
            const points = [];
            for (let i = WEEKS_IN_WEEKLY_RANGE - 1; i >= 0; i--) {
                const weekStart = new Date(currentWeekStart);
                weekStart.setDate(currentWeekStart.getDate() - i * 7);
                points.push({
                    x: formatDate(weekStart),
                    y: weekTotals.get(weekStart.getTime()) ?? 0,
                });
            }
            return {
                timelineData: [{ id: 'Reviews', data: points }],
                tickRotation: -45,
                bottomMargin: 60,
            };
        }

        if (granularity === 'monthly') {
            const monthTotals = new Map<string, number>();
            dailyCountMap.forEach((count, dateKey) => {
                const d = parseDate(dateKey);
                const key = `${d.getFullYear()}-${d.getMonth()}`;
                monthTotals.set(key, (monthTotals.get(key) ?? 0) + count);
            });

            const points = [];
            for (let i = MONTHS_IN_MONTHLY_RANGE - 1; i >= 0; i--) {
                const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
                const key = `${d.getFullYear()}-${d.getMonth()}`;
                const label = `${MONTH_SHORT_NAMES[d.getMonth()]} ${d.getFullYear()}`;
                points.push({
                    x: label,
                    y: monthTotals.get(key) ?? 0,
                });
            }
            return {
                timelineData: [{ id: 'Reviews', data: points }],
                tickRotation: -45,
                bottomMargin: 70,
            };
        }

        // Quarterly — aggregate daily counts into calendar quarter buckets.
        const quarterTotals = new Map<string, number>();
        dailyCountMap.forEach((count, dateKey) => {
            const d = parseDate(dateKey);
            const q = Math.floor(d.getMonth() / 3) + 1;
            const key = `${d.getFullYear()}-Q${q}`;
            quarterTotals.set(key, (quarterTotals.get(key) ?? 0) + count);
        });

        const points = [];
        for (let i = QUARTERS_IN_QUARTERLY_RANGE - 1; i >= 0; i--) {
            const totalMonthsBack = i * 3;
            const d = new Date(
                today.getFullYear(),
                today.getMonth() - totalMonthsBack,
                1,
            );
            const q = Math.floor(d.getMonth() / 3) + 1;
            const key = `${d.getFullYear()}-Q${q}`;
            points.push({
                x: `Q${q} ${d.getFullYear()}`,
                y: quarterTotals.get(key) ?? 0,
            });
        }
        return {
            timelineData: [{ id: 'Reviews', data: points }],
            tickRotation: -30,
            bottomMargin: 60,
        };
    }, [dailyCountMap, granularity]);

    const tooltipLabel = useMemo(() => {
        if (granularity === 'weekly') return 'Week of';
        if (granularity === 'monthly') return 'Month';
        if (granularity === 'quarterly') return 'Quarter';
        return 'Date';
    }, [granularity]);

    // The y-axis should only show integer ticks because reviews are whole
    // assets. We compute explicit tick values so nivo doesn't auto-generate
    // decimal ticks (e.g. 0.2, 0.4) when the max is low across any of the
    // daily / weekly / monthly views. For larger ranges we step by a sensible
    // integer so the axis doesn't get crowded.
    const integerYTicks = useMemo(() => {
        const maxY = timelineData[0].data.reduce(
            (acc, point) => Math.max(acc, Number(point.y) || 0),
            0,
        );
        const upper = Math.max(maxY, 1);
        const targetTickCount = 8;
        const rawStep = upper / targetTickCount;
        const step = Math.max(1, Math.ceil(rawStep));
        const ticks: number[] = [];
        for (let i = 0; i <= upper; i += step) {
            ticks.push(i);
        }
        if (ticks[ticks.length - 1] !== upper) {
            ticks.push(ticks[ticks.length - 1] + step);
        }
        return ticks;
    }, [timelineData]);

    const handleOptionChange = (option: GranularityOption) => {
        if (option.value !== granularity) {
            setGranularity(option.value);
        }
    };

    return (
        <div className="review-timeline">
            <div className="review-timeline-controls">
                {granularityOptions.map((option) => (
                    <div
                        key={option.value}
                        className={`filter-button ${
                            option.value === granularity ? 'applied' : ''
                        }`}
                        onClick={() => handleOptionChange(option)}
                    >
                        <div className="filter-title">{option.label}</div>
                    </div>
                ))}
            </div>
            <div className="chart">
                <ResponsiveLine
                    data={timelineData}
                    margin={{
                        top: 20,
                        right: 20,
                        bottom: bottomMargin,
                        left: 50,
                    }}
                    xScale={{ type: 'point' }}
                    yScale={{
                        type: 'linear',
                        min: 0,
                        max: integerYTicks[integerYTicks.length - 1],
                    }}
                    curve="monotoneX"
                    axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: tickRotation,
                        legendOffset: 50,
                        legendPosition: 'middle',
                    }}
                    axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legendOffset: -40,
                        legendPosition: 'middle',
                        tickValues: integerYTicks,
                        format: (value) => Math.round(Number(value)).toString(),
                    }}
                    gridYValues={integerYTicks}
                    colors={['#e5b611']}
                    pointSize={8}
                    pointColor={{ theme: 'background' }}
                    pointBorderWidth={2}
                    pointBorderColor={{ from: 'serieColor' }}
                    pointLabelYOffset={-12}
                    useMesh={true}
                    enableSlices="x"
                    enableArea={true}
                    areaOpacity={0.1}
                    crosshairType="x"
                    tooltip={({ point }) => (
                        <div
                            style={{
                                padding: 12,
                                color: '#fff',
                                background: '#333',
                                borderRadius: 3,
                            }}
                        >
                            <strong>
                                {tooltipLabel}: {point.data.x.toString()}
                            </strong>
                            <br />
                            <span>
                                Reviews:{' '}
                                <strong>{point.data.y.toString()}</strong>
                            </span>
                        </div>
                    )}
                    theme={{
                        grid: {
                            line: {
                                stroke: '#dddddd',
                                strokeWidth: 1,
                            },
                        },
                        tooltip: {
                            container: {
                                background: '#333',
                                color: '#fff',
                                fontSize: 12,
                                borderRadius: 2,
                                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.25)',
                                padding: '5px 9px',
                            },
                        },
                    }}
                />
            </div>
        </div>
    );
};

export default ReviewTimeLine;
